import { DataTypes } from "../../../data/DataManager";
import { FieldSchema } from "../../../Schema";
import { GridFilterTypes, GridRenderSlots } from "../../../grid/AgGridManager";

/**
 * Factory for the physical dimension / clearance fields. Each has a metric value
 * (kg / mm / W) and an imperial sibling (lbs / inch); the grid renders the value
 * through the CONVERSION slot and swaps the header based on the active unit system,
 * so the schema only records the metric label and the imperial sibling field name.
 *
 * These fields are grid/table-only (not part of the equipment form), so they carry
 * a `grid` block (opt-in as a hidden-by-default column) but no `form` block.
 *
 * @param name - Metric field name (e.g. "weightInkg").
 * @param label - Metric column header (e.g. "Weight in kg").
 * @param imperialKey - Imperial sibling field name (e.g. "weightInlbs").
 * @param order - Column order in the grid.
 * @param integerOnly - Whether the number filter accepts integers only (default true).
 */
export const createDimensionField = (
  name: string,
  label: string,
  imperialKey: string,
  order: number,
  integerOnly: boolean = true
): FieldSchema => ({
  name,
  // Read-only / additional-info field: fetched & displayed, but excluded from
  // create/update mutations (managed outside the schema equipment form).
  dataTransform: { toGraphql: undefined },
  data: { type: DataTypes.NUMBER },
  grid: {
    displayed: true,
    order,
    label,
    filter: GridFilterTypes.NUMBER,
    integerOnly,
    render: GridRenderSlots.CONVERSION,
    imperialKey,
  },
});

export const weightInkg = createDimensionField(
  "weightInkg",
  "Weight in kg",
  "weightInlbs",
  45,
  false // weight is a double, not integer-only
);
export const heightInmm = createDimensionField("heightInmm", "Height in mm", "heightIninch", 46);
export const lengthInmm = createDimensionField("lengthInmm", "Length in mm", "lengthIninch", 47);
export const depthInmm = createDimensionField("depthInmm", "Depth in mm", "depthIninch", 48);
export const clearanceLeftInmm = createDimensionField(
  "clearanceLeftInmm",
  "Clearance left in mm",
  "clearanceLeftIninch",
  49
);
export const clearanceRightInmm = createDimensionField(
  "clearanceRightInmm",
  "Clearance right in mm",
  "clearanceRightIninch",
  50
);
export const clearanceHeightInmm = createDimensionField(
  "clearanceHeightInmm",
  "Clearance height in mm",
  "clearanceHeightIninch",
  51
);
export const clearanceToFrontInmm = createDimensionField(
  "clearanceToFrontInmm",
  "Clearance to front in mm",
  "clearanceToFrontIninch",
  52
);
export const clearanceBackwardsInmm = createDimensionField(
  "clearanceBackwardsInmm",
  "Clearance backwards in mm",
  "clearanceBackwardsIninch",
  53
);
